/**
 * Constants for the Admin dash v2 shell: query caps for the admin-scoped data actions.
 *
 * Every admin action reads through the service client (no RLS), so each list
 * query carries an explicit cap from here rather than an inline literal.
 */

// ── Query limits ──────────────────────────────────────────────────────────

/**
 * Page size for `listAdminOrgs`' paginated reads of `enterprise_organizations` and
 * `org_enterprise_config` (see `fetchAllRows` in `actions/admin-orgs.ts`) — NOT a cap on how
 * many rows the admin org switcher can ever list. A one-shot `.limit()` at 200 used to be that
 * cap, and because the query orders by name, it silently dropped every org alphabetically past
 * it once the table passed 200 rows (506 as of 2026-09-09) — "Pretium ..." among the casualties,
 * invisible and unsearchable in the switcher with no error anywhere. Pagination reads every row
 * regardless of table size; this number only controls how many rows move per round-trip.
 */
export const ADMIN_ORGS_PAGE_SIZE = 1000;

/**
 * Max `org_enterprise_config` bridge rows read for ONE enterprise org's event switcher
 * (`listAdminOrgEvents`) — bounded by how many legacy `orgs` rows one enterprise org can bridge
 * to, which is always a handful, never anywhere near this number.
 */
export const ADMIN_ORG_CONFIG_QUERY_LIMIT = 1000;

/**
 * Max `events` rows scanned when counting active events across every enterprise
 * org for the "All orgs" pill. A cap, not a page — the count degrades (undercounts)
 * rather than the query running unbounded.
 */
export const ADMIN_ORG_EVENT_SCAN_LIMIT = 5000;

/** Max `events` rows listed for one org's event switcher. */
export const ADMIN_ORG_EVENTS_QUERY_LIMIT = 500;

/** Max owners per org — mirrors the DB CHECK on `enterprise_organizations.org_owner_user_ids`. */
export const MAX_ORG_OPS_POCS = 3;

/**
 * Cap for `listOpsStaff`'s `admin_users` read — the staff roster is always a few dozen rows, so
 * this is a one-shot `.limit()`, not a `fetchAllRows` page size like `ADMIN_ORGS_PAGE_SIZE`.
 */
export const ADMIN_OPS_STAFF_LIMIT = 200;

/** Milliseconds in 30 days, for the "active in the last month" org filter. */
export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Page size for `loadEventGroupsByEventId`'s paginated read of `enterprise_event_access` (see
 * `fetchAllRows` in `actions/admin-org-events-cards.ts`) — NOT a cap on how many access rows one
 * org's events can have. Access is granted per group MEMBER, not per event, so one event
 * routinely has several rows behind it (confirmed up to 6 on a single event); capping this read
 * at `ADMIN_ORG_EVENTS_QUERY_LIMIT` (sized for one row per event) silently dropped group data for
 * events past the cap once an org had enough of them. Pagination reads every row regardless of
 * count; this number only controls how many rows move per round-trip.
 */
export const ADMIN_EVENT_ACCESS_PAGE_SIZE = 1000;

// ── Result-list animation ─────────────────────────────────────────────────

/**
 * Max number of visible items (orgs on All Organizations, loaded events on All Events) for which the survivors of a filter animate their *reflow* into new
 * positions (Motion's `layout` prop).
 *
 * This caps the expensive half of the animation, not the visible half. `layout` makes Motion
 * measure every participating card on every render, and the page is unpaginated past 500 rows, so
 * leaving it on would mean 500+ measurements per keystroke in the search box. Above this the
 * survivors snap to their new positions instead of sliding; exits still animate, bounded
 * separately by {@link ADMIN_LIST_EXIT_ANIMATED_MAX}.
 */
export const ADMIN_LIST_REFLOW_ANIMATED_MAX = 60;

/**
 * Fewest results for which the survivors of a filter still slide into their new positions.
 *
 * Below this the reflow is switched off and they simply appear. A slide reads as continuity only
 * when the list mostly survives and shifts a little; narrowing 500 cards to a handful is not that,
 * and animating it drags the few matches across the whole page from wherever they happened to sit
 * before — which looks like travel, not like filtering. Note that a match arriving from deeper
 * than {@link ADMIN_LIST_EXIT_ANIMATED_MAX} changes component type on its way into the top of the
 * list, so it remounts and plays its entrance regardless: it fades in rather than sliding in.
 */
export const ADMIN_LIST_REFLOW_ANIMATED_MIN = 8;

/**
 * How many cards, from the top of the rendered list, get an exit animation when they are filtered
 * out. Cards past this simply disappear.
 *
 * Bounded by what a viewport can actually show rather than by the list length: in a 3-column grid
 * this is several screens' worth, so filtering a 500-card list looks identical to animating all of
 * it while only ever animating this many. A card only needs the animation if it was on screen at
 * the moment it left, and everything below the fold can leave silently.
 */
export const ADMIN_LIST_EXIT_ANIMATED_MAX = 30;

/**
 * Delay before a keystroke in the All Organizations search box actually re-filters the list.
 *
 * The card transitions are slower than normal typing speed, so filtering on every keystroke
 * retargets cards that are still mid-exit and the animation perpetually restarts — jitter rather
 * than motion. A real timer (not `useDeferredValue`, which is only a scheduler hint and does not
 * throttle a list this cheap to re-render) guarantees the filter, and so the animation, runs once
 * typing pauses. The input stays bound to the raw term, so typing itself is never delayed.
 */
export const ADMIN_ORGS_SEARCH_DEBOUNCE_MS = 200;

/**
 * Batch size for the All Events page's `listAdminEventsOverview` infinite-scroll read — deliberately
 * small: each batch also runs fetch-v2's `deriveEventBatch` (candidate + six-signal scan) for the
 * sourcing bar, so this stays far under that function's own `EVENT_BATCH_LIMIT` (200) and under
 * `max_limit` (100) in `nowadays.search_admin_events`.
 */
export const ADMIN_EVENTS_PAGE_SIZE = 25;

/** Rows the tab-counts read asks `search_admin_events` for: the function needs a page of at least one
 *  row, and only its counts are used. */
export const ADMIN_EVENTS_COUNTS_ONLY_PAGE_LIMIT = 1;

/** Debounce for the All Events search box, before it re-keys the infinite query. Mirrors
 *  {@link ADMIN_ORGS_SEARCH_DEBOUNCE_MS}. */
export const ADMIN_EVENTS_SEARCH_DEBOUNCE_MS = 200;

/** Users per page on the all-orgs Users list. Each row carries its group memberships (embedded). */
export const ADMIN_USERS_LIST_PAGE_SIZE = 30;

/** Debounce for the Users search box, before it re-runs the server-side search. */
export const ADMIN_USERS_LIST_SEARCH_DEBOUNCE_MS = 300;

/**
 * Cap on the searched-users count. The empty search counts exactly (one `count: "exact"` head
 * query); a search counts by reading matching ids across the name/email/id queries and merging
 * them, which is only worth doing up to a point. Past this the toolbar reads "1,000+ users".
 */
export const ADMIN_USERS_COUNT_CAP = 1000;

/** Longest search term any Users-page read honors; past it a term can't match a real name,
 *  address, group or event anyway. Shared by the user, group, event-picker and event searches. */
export const ADMIN_USERS_MAX_SEARCH_LENGTH = 100;

/** Shortest search term the Users list and event search honor; anything shorter is treated as no
 *  search (there is no `pg_trgm` index yet, so a 1-character `ilike` would seq-scan for nothing). */
export const ADMIN_USERS_MIN_SEARCH_LENGTH = 2;

/** Most events one "Add events to" request may grant at once (runtime input validation). */
export const ADMIN_USERS_ADD_EVENTS_MAX_COUNT = 100;

/** Page size for the org-filtered add-events picker's event list. */
export const ADMIN_USERS_EVENT_PICKER_PAGE_SIZE = 50;

/** Max event-search candidates returned for the Users list's event-mode search. */
export const ADMIN_USERS_EVENT_SEARCH_LIMIT = 20;

/**
 * Sort options for the all-orgs Users list. Every one is a column that is unique on its own, so it
 * can carry the keyset cursor by itself; a name or last-active sort would need a composite cursor
 * and an index, so neither is offered. Direction is baked into the label, matching
 * {@link ADMIN_EVENTS_SORT_OPTIONS}.
 */
export const ADMIN_USERS_SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "email", label: "Email (A-Z)" },
] as const;

/** Max enterprise-group search results for the add-to-group modal. */
export const ADMIN_USERS_GROUP_SEARCH_LIMIT = 20;

/** Group chips shown per Users-list row before the rest collapse into a "+N" chip. */
export const ADMIN_USERS_GROUP_CHIPS_MAX = 3;

/** Page size for exhaustive reads that must return every row to be correct: the detail page's
 *  access read (KTD3), the event-access search's viewer list, and the group-removal preview
 *  (U8), all read with `fetchAllRows` at this page size rather than a single capped `.limit()`. */
export const ADMIN_USERS_EXHAUSTIVE_READ_PAGE_SIZE = 500;

/** Cap on the `admin_users` roster read for the admin palette's events owner-name search (the roster is ~15 rows). */
export const ADMIN_USERS_LOOKUP_LIMIT = 200;

/** Server-side sort options for the All Events page. "Default" is active events by Fetch v2's
 *  approximate priority, then everything else newest first. "Decision deadline" is upcoming
 *  (soonest first), then passed (most recent first), then none. "Unclaimed first" is events with no
 *  Event owner, then claimed ones, each newest first. The rest each map to an `events` column in
 *  `listAdminEventsOverview`. `event_id` is always the tiebreaker so paging stays stable.
 *  `nowadays.search_admin_events` accepts exactly these values as `p_sort`, and orders by them. */
export const ADMIN_EVENTS_SORT_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "decision_deadline", label: "Decision deadline" },
  { value: "unclaimed_first", label: "Unclaimed first" },
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "name", label: "Name (A-Z)" },
  { value: "status_changed", label: "Last status change" },
] as const;

/** One {@link ADMIN_EVENTS_SORT_OPTIONS} value. */
export type AdminEventsSort = (typeof ADMIN_EVENTS_SORT_OPTIONS)[number]["value"];

/** The sort the page lands on, and the only one the SSR first batch is fetched with. */
export const ADMIN_EVENTS_DEFAULT_SORT: AdminEventsSort = "default";

/** "Decision deadline" filter choices for the All Events page. Windows are measured from now:
 *  `next_7_days`/`next_30_days` = upcoming deadlines within that window, `passed` = before now,
 *  `none` = no deadline set. `nowadays.search_admin_events` accepts exactly these values as
 *  `p_deadline`. */
export const ADMIN_EVENTS_DEADLINE_FILTER_OPTIONS = [
  { value: "any", label: "Any" },
  { value: "next_7_days", label: "Next 7 days" },
  { value: "next_30_days", label: "Next 30 days" },
  { value: "passed", label: "Passed" },
  { value: "none", label: "No deadline" },
] as const;

/** One {@link ADMIN_EVENTS_DEADLINE_FILTER_OPTIONS} value. */
export type AdminEventsDeadlineFilter = (typeof ADMIN_EVENTS_DEADLINE_FILTER_OPTIONS)[number]["value"];

/** Owner-filter value for events with no Event owner, alongside real `users.user_id`s. */
export const ADMIN_EVENTS_UNASSIGNED_OWNER = "unassigned";

/** Cap on how many org or owner ids one filter request may carry (runtime input validation).
 *  Equals `max_filter_ids` in `nowadays.search_admin_events`; keep the two in step. */
export const ADMIN_EVENTS_FILTER_MAX_IDS = 200;

/** Cap on `listAdminEventsOverview`'s `offset` (runtime input validation) — comfortably past the
 *  ~2,500 events in the table today, but bounded so a bad/malicious offset can't force a scan
 *  against an arbitrarily large value. Equals `max_offset` in `nowadays.search_admin_events`. */
export const ADMIN_EVENTS_MAX_OFFSET = 50_000;

/** Cap on `listAdminEventsOverview`'s free-text `search` length (runtime input validation) — well
 *  past any real org/client/owner name, bounded so an oversized string can't inflate every
 *  `ILIKE` it feeds into. Equals `max_search_length` in `nowadays.search_admin_events`. */
export const ADMIN_EVENTS_MAX_SEARCH_LENGTH = 200;

/** Days in the "Next 7 days" / "Next 30 days" decision-deadline windows. */
export const ADMIN_EVENTS_DEADLINE_WINDOW_DAYS = { next_7_days: 7, next_30_days: 30 } as const;

/** Cap on the active events ranked for the "Default" sort (~270 active today). Above it, the
 *  lowest-id actives past the cap are dropped from the ranked group, and an error is logged. Equals
 *  `priority_scan_limit` in `nowadays.search_admin_events`, which is what applies it. */
export const ADMIN_EVENTS_PRIORITY_SCAN_LIMIT = 1000;

/** Cap on the per-page group-access / client-owner enrichment reads (`loadEventGroupsByEventId`,
 *  `loadClientsByEventId`) — bounded by page size × a generous per-event row count, not expected
 *  to ever bind in practice, but every list query needs an explicit cap. */
export const ADMIN_EVENTS_ENRICHMENT_QUERY_LIMIT = ADMIN_EVENTS_PAGE_SIZE * 50;

// ── Row actions ───────────────────────────────────────────────────────────

/** Most rows one bulk edit can change. Above it, edits are disabled in the menu and bar and the server refuses. Copy has no cap. */
export const MAX_BULK_ROW_ACTIONS = 100;

/** Most events one bulk cancel can take: each cancel emails its venues, and large batches can time out mid-send. */
export const MAX_BULK_CANCEL = 10;

/** Bulk edits of more rows than this ask for confirmation first. Cancel and deposit always confirm. */
export const BULK_CONFIRM_THRESHOLD = 25;

/**
 * How long a bulk edit's toasts stay up. The full list refetch after a bulk edit waits this long,
 * so rows do not move under an Undo that is still on screen.
 */
export const BULK_UNDO_TOAST_DURATION_MS = 7000;

/** Shown when a bulk edit starts while the list on screen is still the previous query's (R13). */
export const BULK_LIST_STALE_MESSAGE = "The list is still updating. Try again in a moment.";

/**
 * How many events a bulk cancel writes at once. Each write reads the old status and flips it only
 * if it still matches, so a bulk cancel keeps the single-row race guard.
 */
export const BULK_CANCEL_WRITE_CONCURRENCY = 4;

/** How many events' venue emails a bulk cancel sends at once. Each event already sends in batches of its own. */
export const BULK_CANCEL_NOTIFY_CONCURRENCY = 2;

/**
 * How long a bulk cancel's background venue emails may run, in ms. Past it no new event starts, and
 * the Slack summary posts with time to spare under the events page's 300 second `maxDuration`.
 */
export const BULK_CANCEL_NOTIFY_BUDGET_MS = 240_000;

/** How many writes a bulk Undo or deposit clear runs at once (one per group, or one per organization). */
export const BULK_WRITE_CONCURRENCY = 4;

/**
 * Why a row action is shown but disabled (R6), for the reasons with no `ADMIN_DASH_ERRORS` string.
 * A disabled item always stays in the menu with one of these next to its label.
 */
export const ROW_ACTION_DISABLED_REASONS = {
  VIEW_ONLY: "View-only access",
  NO_ENTERPRISE_ORG: "No enterprise organization",
  NO_CLIENT: "No client",
} as const;

/** How far the pointer must travel with the button down before a press becomes a drag select. */
export const DRAG_THRESHOLD_PX = 4;

/** How far below an event's status chip its status menu opens. */
export const CHIP_MENU_GAP_PX = 4;

/** The gap kept between the fee panel and the viewport's edges when the point is near one. */
export const VIEWPORT_MARGIN_PX = 8;

/** How long a touch press on a row lasts before it opens the row's menu. */
export const LONG_PRESS_MS = 500;

/** How many frames the row menu waits, after opening, to take focus before it opens the submenu a letter key asked for. */
export const SUBMENU_OPEN_MAX_FRAMES = 10;

/** How many frames a closing dialog may keep focus, while its exit animation runs, before the row menu stops waiting to return focus to the row. */
export const DIALOG_FOCUS_RETURN_MAX_FRAMES = 120;

/** How long the selection must stay put before screen readers hear the new count, so a drag or a burst of clicks is announced once. */
export const SELECTION_ANNOUNCE_DELAY_MS = 400;

/**
 * The CSS custom property on the document root that lifts the toast viewport off the bottom of the
 * screen. `FetchToastProvider` reads it with a `0px` fallback, so pages without a bar are unchanged.
 */
export const TOAST_BOTTOM_OFFSET_VAR = "--toast-bottom-offset";

/**
 * Added to the selection bar's height for the toast offset. It matches the bar's `bottom-4`, so the
 * toasts start at the bar's top edge and the viewport's own padding keeps them clear of it.
 */
export const SELECTION_BAR_TOAST_GAP_PX = 16;

/** How far the selection bar slides up as it appears. */
export const SELECTION_BAR_SLIDE_PX = 8;

/** How long the selection bar's slide up lasts, in seconds (Motion's unit). */
export const SELECTION_BAR_SLIDE_S = 0.16;

/**
 * Set on the document root while the selection bar is mounted, which is exactly while anything is
 * selected. Every row's checkbox and `⋮` read it through {@link ROW_CHROME_REVEAL_CLASS}.
 */
export const SELECTION_ACTIVE_ATTRIBUTE = "data-selection-active";

/**
 * The named Tailwind group a row or card element carries (next to `useRowActionBindings`' props),
 * so the chrome inside it can react to the row's hover, focus, `data-selected`, and `data-active`.
 */
export const ROW_CHROME_GROUP_CLASS = "group/row";

/**
 * Hides a row's selection chrome (checkbox, `⋮`) at rest and reveals it, in CSS
 * only, so no row renders for it (R14, R30). It shows on hover, on keyboard focus inside the row,
 * on the active row, on a selected row, on every row once anything is selected (the
 * `data-selection-active` root attribute), and always on touch screens. It only fades, so it never
 * shifts layout, and reduced motion makes the fade instant. The row must carry
 * {@link ROW_CHROME_GROUP_CLASS}.
 */
export const ROW_CHROME_REVEAL_CLASS =
  "opacity-0 transition-opacity duration-[120ms] ease-out motion-reduce:transition-none focus-within:opacity-100 group-hover/row:opacity-100 group-focus-within/row:opacity-100 group-data-[active]/row:opacity-100 group-data-[selected]/row:opacity-100 [[data-selection-active]_&]:opacity-100 [@media(hover:none)]:opacity-100";

/** A selected table row's look: the indigo-50 fill. Apply on the row that spreads the bindings. */
export const SELECTED_TABLE_ROW_CLASS = "data-[selected]:bg-indigo-50";

/**
 * A selected card's look, Linear style: the table row's indigo-50 fill plus an indigo border. The
 * card scopes its own hover and active borders to `:not([data-selected])`, so this border needs no
 * `!important`. Only colors change, so selecting never shifts layout. The card sets which
 * properties transition. Apply on the card element that spreads `useRowActionBindings`, which sets
 * `data-selected`.
 */
export const SELECTED_CARD_CLASS = `${SELECTED_TABLE_ROW_CLASS} duration-[120ms] ease-out motion-reduce:transition-none data-[selected]:border-indigo-300`;

// ── Client-facing error strings ───────────────────────────────────────────

/**
 * Generic failure messages returned to the client. Hardcoded on purpose: the
 * real Postgres error is logged server-side and never crosses the boundary, so
 * no schema detail, constraint name or internal id leaks.
 */
export const ADMIN_DASH_ERRORS = {
  UNAUTHORIZED: "Unauthorized",
  INVALID_ID: "Invalid id",
  INVALID_REQUEST: "Invalid request",
  LOAD_ORGS_FAILED: "Failed to load organizations",
  LOAD_EVENTS_FAILED: "Failed to load events",
  LOAD_EVENT_FAILED: "Failed to load event",
  LOAD_HOME_FAILED: "Failed to load home",
  LOAD_EVENT_NOTES_FAILED: "Failed to load notes",
  LOAD_COMMUNICATIONS_FAILED: "Failed to load communications",
  LOAD_PREFERRED_VENUES_FAILED: "Failed to load preferred venues",
  LOAD_BLACKLISTED_VENUES_FAILED: "Failed to load blacklisted venues",
  LOAD_POLICIES_FAILED: "Failed to load policies",
  LOAD_USERS_FAILED: "Failed to load users",
  GRANT_ALL_EVENTS_FAILED: "Failed to grant event access",
  LOAD_ORGS_OVERVIEW_FAILED: "Failed to load organizations",
  LOAD_EVENTS_OVERVIEW_FAILED: "Failed to load events",
  LOAD_ORG_BILLING_FAILED: "Failed to load deposit and fee settings",
  UPDATE_ORG_BILLING_FAILED: "Failed to update deposit and fee settings",
  ORG_NOT_FOUND: "Organization not found",
  SET_ORG_POC_FAILED: "Failed to update owners",
  TOO_MANY_ORG_POCS: `An organization can have at most ${MAX_ORG_OPS_POCS} owners`,
  LOAD_USER_DETAIL_FAILED: "Failed to load user",
  LOAD_EVENT_SEARCH_FAILED: "Failed to search events",
  LOAD_EVENT_PICKER_FAILED: "Failed to load events",
  LOAD_GROUP_SEARCH_FAILED: "Failed to search groups",
  LOAD_GROUP_REMOVAL_PREVIEW_FAILED: "Failed to load removal details",
  INVALID_ROLE: "Invalid role",
  ADD_EVENTS_FAILED: "Failed to add events",
  TOO_MANY_EVENTS: `An add-events request can include at most ${ADMIN_USERS_ADD_EVENTS_MAX_COUNT} events`,
  REMOVE_ACCESS_FAILED: "Failed to remove access",
  ORG_MISMATCH: "That event isn't in this group's organization",
  ADD_TO_GROUP_FAILED: "Failed to add to group",
  REMOVE_GROUP_FAILED: "Failed to remove from group",
  LAST_ADMIN: "This is the group's last admin. Add another admin before removing this one.",
  LOAD_ORG_REMOVAL_PREVIEW_FAILED: "Failed to load removal details",
  REMOVE_ORG_FAILED: "Failed to remove from organization. Some groups may already be removed, so refresh and check.",
  LAST_ORG_ADMIN: "They're this organization's last admin. Add another admin before removing them.",
  UPDATE_PROFILE_FAILED: "Failed to update profile",
  DUPLICATE_EMAIL: "That email is already in use",
  INVALID_PHOTO_TYPE: "Invalid file type. Only JPG, JPEG, and PNG are allowed.",
  UPDATE_PHOTO_FAILED: "Failed to update photo",
  DELETE_BLOCKED_OWNS_EVENTS: "This user owns events. Transfer ownership before deleting.",
  DELETE_BLOCKED_SELF: "You can't delete your own account",
  DELETE_BLOCKED_STAFF: "This user is a Nowadays admin and can't be deleted here",
  DELETE_FAILED: "Failed to delete user",
  /** A delete that failed part-way. Deliberately says nothing about WHERE it stopped: the step
   *  name is a table name, which belongs in the server log, not in a client-facing string. */
  DELETE_PARTIAL: "Delete stopped part-way. Some of the user's data was already removed.",
  // Bulk row actions. Each failed row carries one of these as its reason.
  BULK_UPDATE_FAILED: "Failed to update",
  TOO_MANY_ROWS: `Select up to ${MAX_BULK_ROW_ACTIONS} to edit`,
  TOO_MANY_CANCELS: `Cancel up to ${MAX_BULK_CANCEL} at a time`,
  EVENT_NOT_FOUND: "Event not found",
  /** Undo skipped the row because someone changed it after this action. */
  CHANGED_SINCE: "Changed since, so it was left as is",
  /** Deposit undo skipped the organization because it paid by card after it was marked. */
  PAID_BY_CARD: "Paid by card since, so it was left as paid",
  NORTHSTAR_FEE_LOCKED: "Fee locked on Northstar events",
} as const;

// ── Deposit and fee chips ─────────────────────────────────────────────────

/** URL hash of the "Deposit & fees" section on an org's Profile settings page, where a partial rate is edited. */
export const ORG_DEPOSIT_FEES_HASH = "#deposit-fees";

/**
 * The words on the confirmation shown before clearing an org's RFP deposit, from the settings
 * section and the org and event chips alike. Clearing loses the original payment date, so it is
 * never a single click. The message itself lives in `ClearDepositDialog`, which names the org.
 */
export const CLEAR_DEPOSIT_DIALOG = {
  title: "Clear the RFP deposit?",
  confirmText: "Clear deposit",
} as const;

/** How long the pointer rests on a fee chip before its slider panel opens, so passing over a chip is quiet. */
export const HOVER_OPEN_DELAY_MS = 150;

/** How long the panel waits before closing after the pointer leaves, so the pointer can cross onto it. */
export const HOVER_CLOSE_DELAY_MS = 200;

/** Shown by the fee slider panel when it is saved while a chip's own change is still in flight. */
export const SAVE_IN_FLIGHT_MESSAGE = "Another change is still saving. Try again in a moment.";

// ── All Events table row ──────────────────────────────────────────────────

/** Max date ranges listed on their own line in `AdminEventsTable`'s Dates column before the rest
 *  collapse into "+N more" — bounds how tall a row with many date ranges can get. */
export const ADMIN_EVENTS_TABLE_MAX_VISIBLE_DATES = 5;

/** Max locations listed on their own line in `AdminEventsTable`'s Location column before the rest
 *  collapse into "+N more" — mirrors {@link ADMIN_EVENTS_TABLE_MAX_VISIBLE_DATES}. */
export const ADMIN_EVENTS_TABLE_MAX_VISIBLE_LOCATIONS = 5;

// ── Org Users palette landing ─────────────────────────────────────────────

/** Shown when a palette grant-all-events landing names a user who is not on this organization's loaded roster. */
export const GRANT_ALL_EVENTS_USER_NOT_IN_ORG_MESSAGE =
  "This user isn't in this organization. If they just joined, refresh the page.";

/** Shown when a palette grant-all-events landing names a user whose invite is still pending. */
export const GRANT_ALL_EVENTS_INVITE_PENDING_MESSAGE = "This user hasn't accepted their invite yet.";

// ── User detail palette landing ───────────────────────────────────────────

/** Shown when a palette add-events-to-user landing names an organization the user isn't in (or a malformed one). */
export const ADD_EVENTS_LANDING_USER_NOT_IN_ORG_MESSAGE = "This user isn't in that organization.";

/** Shown when a palette add-events-to-user landing's `?group=` is not one of the user's groups in that organization. */
export const ADD_EVENTS_LANDING_GROUP_NOT_FOUND_MESSAGE =
  "That group isn't one of this user's groups in this organization.";

/** Shown when a palette add-events-to-user landing finds the user in no group in that organization. */
export const ADD_EVENTS_LANDING_NO_GROUPS_MESSAGE =
  "This user isn't in a group in this organization. Add them to a group first.";

/** Shown when a palette add-events-to-user landing has no `?group=` and the user is in several groups there. */
export const ADD_EVENTS_LANDING_SEVERAL_GROUPS_MESSAGE =
  "This user is in several groups here. Choose one from its menu.";

// ── Toolbar filters and sort ──────────────────────────────────────────────

/** Gap between a toolbar's Filters pill and the filter panel it opens, in pixels. */
export const FILTER_PANEL_GAP_PX = 8;

/** A filter panel's width, in pixels, before it is capped to fit the viewport. */
export const FILTER_PANEL_WIDTH_PX = 384;

/** Minimum gap kept between a filter panel and the viewport's side edges, in pixels. */
export const FILTER_PANEL_VIEWPORT_MARGIN_PX = 16;

/** Default open-panel width for a toolbar's "Sort by" menu, wide enough for its longest label. */
export const SORT_MENU_MIN_WIDTH_CLASSNAME = "min-w-[200px]";
